// GIISDEC Unified Form Wizard Framework (WP2.2-R3)
// ---------------------------------------------------------------------------
// A reusable, production-ready multi-step form wizard for the GIISDEC platform.
//
// Design contract (why this is safe for existing API payloads):
//   • The wizard renders ONE <form>. Every step's fields stay MOUNTED at all
//     times; only the active step is visible (via the `hidden` attribute).
//     Therefore the consumer's submit handler can read `new FormData(form)`
//     exactly as before — field `name` attributes and payload shape are the
//     load-bearing contract and are never touched by the framework.
//   • Validation is native `checkValidity()` per step (no controlled state),
//     mirroring the existing forms' uncontrolled/`FormData` pattern.
//   • No draft is faked: values persist only because inputs stay mounted for
//     the life of the form (session-local, clearly not server-persisted).
//
// Accessibility: ordered stepper with aria-current="step", role="group"
// step regions, a role="alert" validation summary, aria-invalid on offending
// fields, focus management, keyboard-operable controls, and a permanently
// visible "Step X of Y" indicator.
//
// Reusable later by Admin Web, GIS workflows and public applications without
// duplicating step logic.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Check, ChevronLeft, ChevronRight, Loader2, AlertTriangle,
  CheckCircle2, ShieldAlert, Ban, Pencil,
} from 'lucide-react';

/* ───────────────── types ───────────────── */

export type WizardStepDef = {
  /** stable key — also used to target Edit links from the review step */
  key: string;
  /** short stepper label */
  label: string;
  /** optional one-line description under the step heading */
  description?: string;
  /** the step's fields — ALWAYS mounted; the wizard toggles visibility */
  content: React.ReactNode;
};

export type WizardReviewSection = {
  /** key of the step this section summarises — wires the Edit link */
  stepKey: string;
  title: string;
  rows: { label: string; value: React.ReactNode }[];
};

export type WizardSubmitState =
  | { phase: 'idle' }
  | { phase: 'submitting' }
  | { phase: 'success'; message: string }
  | { phase: 'error'; message: string; requestId?: string }
  | { phase: 'unavailable'; message: string }
  | { phase: 'restricted'; message: string }
  | { phase: 'duplicate'; message: string };

export interface FormWizardProps {
  title: string;
  description?: string;
  /** optional status badge rendered in the header (e.g. KYC status) */
  statusBadge?: React.ReactNode;
  /** the data-entry steps (a Review step is appended automatically) */
  steps: WizardStepDef[];
  /** builds the review summary from the current live form values */
  buildReview: (values: Record<string, string>) => WizardReviewSection[];
  /** declaration / consent node shown on the review step (optional) */
  declaration?: React.ReactNode;
  submitLabel?: string;
  submitState: WizardSubmitState;
  /** called with the live <form> element after full validation passes */
  onSubmit: (form: HTMLFormElement) => void;
  /** optional: start a new entry after success */
  onReset?: () => void;
  /** review step label override */
  reviewLabel?: string;
}

/* ───────────────── shared field helpers (EDS) ───────────────── */

export const wizardInputCls =
  'w-full px-3 py-2 rounded-eds-md border border-eds-border bg-eds-surface-2 text-sm text-eds-fg ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-emerald/40 ' +
  'aria-[invalid=true]:border-eds-danger aria-[invalid=true]:ring-eds-danger/30';

export const WizardField: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-eds-fg uppercase tracking-wider">
      {label}{required && <span className="text-eds-danger" aria-hidden="true"> *</span>}
    </span>
    <div className="mt-1">{children}</div>
    {hint && <span className="mt-1 block text-sm font-medium text-eds-muted-fg">{hint}</span>}
  </label>
);

export const WizardFieldGroup: React.FC<{
  legend: string;
  description?: string;
  children: React.ReactNode;
  cols?: 1 | 2 | 3;
}> = ({ legend, description, children, cols = 2 }) => (
  <fieldset className="space-y-4">
    <legend className="text-sm font-semibold text-eds-fg">{legend}</legend>
    {description && <p className="text-sm font-medium text-eds-muted-fg -mt-2">{description}</p>}
    <div className={`grid grid-cols-1 ${cols === 3 ? 'md:grid-cols-3' : cols === 2 ? 'md:grid-cols-2' : ''} gap-4`}>
      {children}
    </div>
  </fieldset>
);

/* ───────────────── submission-state banner ───────────────── */

const stateStyles: Record<string, { cls: string; icon: React.ComponentType<{ className?: string }> }> = {
  success: { cls: 'border-eds-emerald/30 bg-eds-emerald/10 text-eds-emerald-2', icon: CheckCircle2 },
  error: { cls: 'border-eds-danger/30 bg-eds-danger/10 text-eds-danger', icon: AlertTriangle },
  unavailable: { cls: 'border-eds-border bg-eds-muted text-eds-muted-fg', icon: Ban },
  restricted: { cls: 'border-eds-gold/30 bg-eds-gold/10 text-eds-earth', icon: ShieldAlert },
  duplicate: { cls: 'border-eds-gold/30 bg-eds-gold/10 text-eds-earth', icon: AlertTriangle },
};

const SubmissionBanner: React.FC<{ state: WizardSubmitState }> = ({ state }) => {
  if (state.phase === 'idle' || state.phase === 'submitting') return null;
  const s = stateStyles[state.phase];
  if (!s) return null;
  const Icon = s.icon;
  const requestId = state.phase === 'error' ? state.requestId : undefined;
  return (
    <div role={state.phase === 'success' ? 'status' : 'alert'} className={`flex items-start gap-2.5 px-4 py-3 rounded-eds-md border text-sm ${s.cls}`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div>
        <div>{'message' in state ? state.message : ''}</div>
        {requestId && <div className="text-[11px] opacity-80 mt-0.5">Reference: {requestId}</div>}
      </div>
    </div>
  );
};

/* ───────────────── stepper ───────────────── */

const WizardStepper: React.FC<{
  labels: string[];
  current: number;
  maxVisited: number;
  onJump: (i: number) => void;
}> = ({ labels, current, maxVisited, onJump }) => (
  <ol className="flex items-center gap-1 overflow-x-auto pb-1" aria-label="Progress">
    {labels.map((label, i) => {
      const done = i < current;
      const active = i === current;
      const reachable = i <= maxVisited;
      return (
        <li key={label} className="flex items-center flex-shrink-0">
          <button
            type="button"
            onClick={() => reachable && onJump(i)}
            disabled={!reachable}
            aria-current={active ? 'step' : undefined}
            className={`group inline-flex items-center gap-2 px-2.5 py-1.5 rounded-eds-md transition ${
              reachable ? 'cursor-pointer hover:bg-eds-muted' : 'cursor-not-allowed opacity-55'
            }`}
          >
            <span
              className={`w-6 h-6 flex-shrink-0 rounded-eds-pill flex items-center justify-center text-xs font-bold ${
                done ? 'bg-eds-emerald text-eds-primary-fg'
                  : active ? 'bg-eds-forest text-white ring-2 ring-eds-emerald/40'
                  : 'bg-eds-muted text-eds-muted-fg'
              }`}
            >
              {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span className={`text-xs font-medium whitespace-nowrap ${active ? 'text-eds-fg' : 'text-eds-muted-fg'}`}>{label}</span>
          </button>
          {i < labels.length - 1 && <span className="w-4 h-px bg-eds-border mx-0.5 flex-shrink-0" aria-hidden="true" />}
        </li>
      );
    })}
  </ol>
);

/* ───────────────── review summary ───────────────── */

const WizardReview: React.FC<{
  sections: WizardReviewSection[];
  onEdit: (stepKey: string) => void;
  declaration?: React.ReactNode;
}> = ({ sections, onEdit, declaration }) => (
  <div className="space-y-5">
    {sections.map((sec) => (
      <div key={sec.stepKey} className="rounded-eds-lg border border-eds-border bg-eds-surface">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-eds-border">
          <h4 className="text-sm font-semibold text-eds-fg">{sec.title}</h4>
          <button
            type="button"
            onClick={() => onEdit(sec.stepKey)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-eds-emerald hover:text-eds-emerald-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-emerald/40 rounded-eds-sm px-1"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        </div>
        <dl className="divide-y divide-eds-border">
          {sec.rows.map((r, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-3 px-4 py-2">
              <dt className="text-xs text-eds-muted-fg col-span-1">{r.label}</dt>
              <dd className="text-sm text-eds-fg col-span-2 break-words">{r.value || <span className="text-eds-muted-fg">—</span>}</dd>
            </div>
          ))}
        </dl>
      </div>
    ))}
    {declaration && <div className="rounded-eds-md border border-dashed border-eds-border bg-eds-muted/50 p-4">{declaration}</div>}
  </div>
);

/* ───────────────── main wizard ───────────────── */

export const FormWizard: React.FC<FormWizardProps> = ({
  title, description, statusBadge, steps, buildReview, declaration,
  submitLabel = 'Submit', submitState, onSubmit, onReset, reviewLabel = 'Review',
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const reviewIndex = steps.length; // review is the last, synthetic step
  const total = steps.length + 1;

  const [current, setCurrent] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [reviewValues, setReviewValues] = useState<Record<string, string>>({});

  const labels = useMemo(() => [...steps.map((s) => s.label), reviewLabel], [steps, reviewLabel]);
  const locked = submitState.phase === 'submitting' || submitState.phase === 'success';

  const readValues = useCallback((): Record<string, string> => {
    const out: Record<string, string> = {};
    if (formRef.current) {
      const fd = new FormData(formRef.current);
      fd.forEach((v, k) => { if (typeof v === 'string') out[k] = v; });
      // checkboxes absent from FormData when unchecked — record presence
      formRef.current.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach((cb) => {
        out[cb.name] = cb.checked ? (cb.value && cb.value !== 'on' ? cb.value : 'on') : '';
      });
    }
    return out;
  }, []);

  // Validate one step's fields via native constraint validation.
  const validateStep = useCallback((index: number): boolean => {
    if (index >= steps.length) return true; // review step has no inputs of its own except declaration (validated separately)
    const node = stepRefs.current[index];
    if (!node) return true;
    const fields = Array.from(node.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea'));
    const bad: string[] = [];
    let firstInvalid: HTMLElement | null = null;
    fields.forEach((el) => {
      el.removeAttribute('aria-invalid');
      if (!el.checkValidity()) {
        el.setAttribute('aria-invalid', 'true');
        if (!firstInvalid) firstInvalid = el;
        const labelText = el.labels?.[0]?.textContent?.replace('*', '').trim() || el.getAttribute('aria-label') || el.name;
        bad.push(labelText);
      }
    });
    if (firstInvalid) {
      setErrors(bad);
      (firstInvalid as HTMLElement).focus();
      (firstInvalid as HTMLElement).scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }
    setErrors([]);
    return true;
  }, [steps.length]);

  const goTo = useCallback((index: number) => {
    setErrors([]);
    setCurrent(index);
    setMaxVisited((m) => Math.max(m, index));
    if (index === reviewIndex) setReviewValues(readValues());
    if (formRef.current) formRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [reviewIndex, readValues]);

  const handleContinue = () => {
    if (!validateStep(current)) return;
    goTo(Math.min(current + 1, reviewIndex));
  };
  const handleBack = () => { setErrors([]); setCurrent((c) => Math.max(0, c - 1)); };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (locked) return; // double-submit / post-success guard
    // Full validation across every data-entry step, plus the review declaration.
    for (let i = 0; i < steps.length; i++) {
      if (!validateStep(i)) { goTo(i); return; }
    }
    // declaration checkbox (if present on the review step) is a native required control
    const reviewNode = stepRefs.current[reviewIndex];
    const decl = reviewNode?.querySelector<HTMLInputElement>('input[type=checkbox][required]');
    if (decl && !decl.checkValidity()) {
      decl.setAttribute('aria-invalid', 'true');
      setErrors(['Declaration must be accepted before submitting.']);
      decl.focus();
      return;
    }
    setErrors([]);
    if (formRef.current) onSubmit(formRef.current);
  };

  const onReview = current === reviewIndex;
  const success = submitState.phase === 'success';

  return (
    <div className="rounded-eds-lg border border-eds-border bg-eds-surface shadow-eds-sm overflow-hidden">
      {/* Branded header */}
      <div className="bg-eds-forest text-white px-5 py-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-eds-display text-lg font-bold">{title}</h2>
          {description && <p className="text-sm text-white/70 mt-0.5 max-w-xl">{description}</p>}
        </div>
        {statusBadge && <div>{statusBadge}</div>}
      </div>

      {/* Stepper */}
      <div className="px-5 pt-4 border-b border-eds-border">
        <WizardStepper labels={labels} current={current} maxVisited={maxVisited} onJump={(i) => { if (i === reviewIndex) setReviewValues(readValues()); goTo(i); }} />
      </div>

      <form ref={formRef} noValidate onSubmit={handleSubmit} className="px-5 py-5">
        {/* validation summary */}
        {errors.length > 0 && (
          <div role="alert" className="mb-5 flex items-start gap-2.5 px-4 py-3 rounded-eds-md border border-eds-danger/30 bg-eds-danger/10 text-eds-danger text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Please complete the highlighted fields:</div>
              <ul className="list-disc ml-5 mt-1">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          </div>
        )}

        {/* success / error / governed banners */}
        <div className="mb-5"><SubmissionBanner state={submitState} /></div>

        {/* data-entry steps — all mounted, visibility toggled to preserve FormData */}
        {steps.map((s, i) => (
          <div
            key={s.key}
            ref={(el) => { stepRefs.current[i] = el; }}
            hidden={success || current !== i}
            role="group"
            aria-labelledby={`wizstep-${s.key}`}
          >
            <div className="mb-4">
              <h3 id={`wizstep-${s.key}`} className="font-eds-display text-base font-semibold text-eds-fg">{s.label}</h3>
              {s.description && <p className="text-sm text-eds-muted-fg mt-0.5">{s.description}</p>}
            </div>
            <div className="space-y-6">{s.content}</div>
          </div>
        ))}

        {/* review step */}
        <div
          ref={(el) => { stepRefs.current[reviewIndex] = el; }}
          hidden={success || current !== reviewIndex}
          role="group"
          aria-labelledby="wizstep-review"
        >
          <div className="mb-4">
            <h3 id="wizstep-review" className="font-eds-display text-base font-semibold text-eds-fg">{reviewLabel} &amp; submit</h3>
            <p className="text-sm text-eds-muted-fg mt-0.5">Confirm the details below. Use Edit to correct any section before submitting.</p>
          </div>
          <WizardReview sections={buildReview(reviewValues)} onEdit={(k) => { const idx = steps.findIndex((s) => s.key === k); if (idx >= 0) goTo(idx); }} declaration={declaration} />
        </div>

        {/* success panel */}
        {success && (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-eds-emerald mx-auto" />
            <p className="mt-3 text-sm text-eds-fg">{submitState.phase === 'success' ? submitState.message : ''}</p>
            {onReset && (
              <button type="button" onClick={() => { setCurrent(0); setMaxVisited(0); setErrors([]); onReset(); }} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-eds-md border border-eds-border text-sm font-medium text-eds-fg hover:bg-eds-muted transition">
                Start another submission
              </button>
            )}
          </div>
        )}

        {/* footer controls */}
        {!success && (
          <div className="mt-6 pt-4 border-t border-eds-border flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={current === 0 || locked}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-eds-md border border-eds-border text-sm font-medium text-eds-fg hover:bg-eds-muted disabled:opacity-45 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            <span className="text-xs font-medium text-eds-muted-fg tabular-nums" aria-live="polite">
              Step {current + 1} of {total}
            </span>

            {onReview ? (
              <button
                type="submit"
                disabled={locked}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-eds-md bg-eds-emerald text-eds-primary-fg text-sm font-semibold hover:bg-eds-emerald-2 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {submitState.phase === 'submitting' ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : submitLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleContinue}
                disabled={locked}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-eds-md bg-eds-forest text-white text-sm font-semibold hover:bg-eds-forest-2 disabled:opacity-60 transition"
              >
                {current === reviewIndex - 1 ? 'Review' : 'Continue'} <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default FormWizard;
